import type { NavigateFunction } from 'react-router-dom';
import type { PendingGen } from '@/context/pending-generations.context';

const ABSOLUTE_URL_RE = /^(https?:|blob:|data:)/i;

/**
 * Opens a finished generation in the right place:
 *  - Absolute URLs (gamma export, gamma.app, blob:, data:) → window.open in a new tab.
 *  - Internal SPA routes (/app/...) → react-router navigate.
 *  - Missing route → returns false so the caller can show feedback.
 */
export function openGenerationResult(gen: PendingGen, navigate: NavigateFunction): boolean {
  const target = gen.fullRoute || gen.artifactUrl || gen.route;
  if (!target) return false;

  if (ABSOLUTE_URL_RE.test(target)) {
    window.open(target, '_blank', 'noopener,noreferrer');
    return true;
  }

  navigate(target);
  return true;
}

/** Build a viewable PDF blob URL for markdown content (generates an authentic application/pdf blob). */
export async function markdownToBlobUrl(markdown: string, title?: string): Promise<string> {
  const safeTitle = (title || 'Material').replace(/[<>&]/g, '').trim();
  try {
    const { exportToPdfBlob } = await import('@/services/exportService');
    const blob = await exportToPdfBlob(markdown, safeTitle);
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('[markdownToBlobUrl] Error compiling PDF blob, falling back to formatted document:', error);
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${safeTitle}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 860px; margin: 2rem auto; padding: 0 1.5rem; line-height: 1.6; color: #1e293b; background: #fff; }
  h1,h2,h3 { color: #002B5B; }
  h1 { border-bottom: 2px solid #e2e8f0; padding-bottom: .5rem; }
  blockquote { border-left: 4px solid #3b82f6; margin: 1rem 0; padding: .5rem 1rem; color:#475569; background:#f8fafc; border-radius: 4px; }
  table { border-collapse: collapse; width:100%; margin: 1rem 0; }
  th,td { border:1px solid #cbd5e1; padding:.6rem; text-align:left; font-size: 14px; }
  th { background: #f1f5f9; }
</style></head><body><div style="white-space:pre-wrap;">${markdown
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')}</div></body></html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    return URL.createObjectURL(blob);
  }
}
